import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@wtc/ui";
import { ConnectionShare } from "@wtc/supabase";

// Types

interface ShareConnectionRequest {
  connectionId: string;
  providerConfigKey: string;
  shareWithUserId: string;
}

interface UnshareConnectionRequest {
  connectionId: string;
  shareWithUserId: string;
}

interface SetDriveAccessRequest {
  connectionId: string;
  shareWithUserId: string;
  driveAccessEnabled: boolean;
}

/** Shape returned by the share/unshare endpoints. */
interface ConnectionShareMutationResponse {
  success?: boolean;
  message?: string;
  share?: ConnectionShare;
}

// API functions
async function fetchConnectionShares(
  connectionId: string,
  token?: string
): Promise<ConnectionShare[]> {
  const response = await fetch(`/api/connections/connections/share?connectionId=${connectionId}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to fetch connection shares");
  }

  const data = await response.json();
  return data.shares || [];
}

async function shareConnection(
  request: ShareConnectionRequest,
  token?: string
): Promise<ConnectionShareMutationResponse> {
  const response = await fetch("/api/connections/connections/share", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to share connection");
  }

  return response.json();
}

async function unshareConnection(
  request: UnshareConnectionRequest,
  token?: string
): Promise<ConnectionShareMutationResponse> {
  const response = await fetch("/api/connections/connections/share", {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to unshare connection");
  }

  return response.json();
}

async function setDriveAccess(
  request: SetDriveAccessRequest,
  token?: string
): Promise<ConnectionShareMutationResponse> {
  const response = await fetch("/api/connections/connections/share", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to update Drive access");
  }

  return response.json();
}

// React Query hooks
export function useConnectionShares(connectionId: string | null, enabled: boolean = true) {
  const { getAuthToken } = useAuth();

  return useQuery({
    queryKey: ["connection-shares", connectionId],
    queryFn: async () => {
      const token = await getAuthToken();
      return fetchConnectionShares(connectionId!, token || undefined);
    },
    enabled: enabled && !!connectionId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useShareConnection() {
  const queryClient = useQueryClient();
  const { getAuthToken } = useAuth();

  return useMutation({
    mutationFn: async (request: ShareConnectionRequest) => {
      const token = await getAuthToken();
      return shareConnection(request, token || undefined);
    },
    onSuccess: (_, variables) => {
      // Invalidate connection shares
      void queryClient.invalidateQueries({
        queryKey: ["connection-shares", variables.connectionId],
      });
      // Invalidate connections list to show updated sharing status
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}

export function useSetConnectionShareDriveAccess() {
  const queryClient = useQueryClient();
  const { getAuthToken } = useAuth();

  return useMutation({
    mutationFn: async (request: SetDriveAccessRequest) => {
      const token = await getAuthToken();
      return setDriveAccess(request, token || undefined);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["connection-shares", variables.connectionId],
      });
    },
  });
}

export function useUnshareConnection() {
  const queryClient = useQueryClient();
  const { getAuthToken } = useAuth();

  return useMutation({
    mutationFn: async (request: UnshareConnectionRequest) => {
      const token = await getAuthToken();
      return unshareConnection(request, token || undefined);
    },
    onSuccess: (_, variables) => {
      // Invalidate connection shares
      void queryClient.invalidateQueries({
        queryKey: ["connection-shares", variables.connectionId],
      });
      // Invalidate connections list to show updated sharing status
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}
